import { appModule2FlowEdge, appModule2FlowNode } from "../../utils/adapt";
import { useCallback, useContext, useMemo } from "react";
import { FlowContext } from "./context";
import { produce } from "immer";
import { nanoid } from "nanoid";
import {
  FlowModuleItemType,
  ModuleInputKeyEnum,
  ModuleItemType,
  ModuleOutputKeyEnum,
} from "@packages/flow-core/module";
import { Node } from "reactflow";
import { FlowNodeTypeEnum } from "@packages/flow-core/module/node/constants";
import { message } from "antd";
import {
  FlowNodeInputItemType,
  FlowNodeOutputItemType,
} from "@packages/flow-core/module/node/types";

export function useFlow() {
  const {
    getAllEdges,
    setNodes,
    onNodesChange,
    getAllNodes,
    onEdgesChange,
    setEdges,
    mode,
    templates,
    llmModels,
    systemVariables,
    useablePlugins,
  } = useContext(FlowContext);

  const hasToolNode = useMemo(() => {
    return !!getAllNodes().find(
      (node) => node.data.flowType === FlowNodeTypeEnum.tools
    );
  }, [getAllNodes]);

  const updateModule = useCallback(
    (moduleId: string, data: Partial<FlowModuleItemType>) => {
      setNodes(
        produce((nodes) => {
          const node = nodes.find((item) => item.data.moduleId === moduleId);
          if (node) {
            Object.assign(node.data, data);
          }
        })
      );
    },
    []
  );

  const splitToolInputs = useCallback(
    (inputs: FlowNodeInputItemType[], moduleId: string) => {
      const edges = getAllEdges();
      const isTool = !!edges.find(
        (edge) =>
          edge.targetHandle === ModuleOutputKeyEnum.selectedTools &&
          edge.target === moduleId
      );

      return {
        isTool,
        toolInputs: inputs.filter((item) => isTool && item.toolDescription),
        commonInputs: inputs.filter((item) => {
          if (!isTool) return true;
          return (
            !item.toolDescription && item.key !== ModuleInputKeyEnum.switch
          );
        }),
      };
    },
    [getAllEdges]
  );

  const deleteModuleOutput = useCallback(
    (moduleId: string, outputKey: string) => {
      setNodes(
        produce((nodes) => {
          const module = nodes.find((item) => item.id === moduleId)!;
          module.data.outputs = module.data.outputs.filter(
            (item) => item.key !== outputKey
          );
        })
      );
      setEdges((edges) => {
        return edges.filter(
          (edge) =>
            edge.source !== moduleId ||
            (edge.source === moduleId && edge.sourceHandle !== outputKey)
        );
      });
    },
    []
  );

  const updateModuleOutput = useCallback(
    (moduleId: string, nodeKey: string, output: FlowNodeOutputItemType) => {
      if (output.key !== nodeKey) {
        setEdges((edges) => {
          return edges.filter(
            (edge) =>
              edge.source !== moduleId ||
              (edge.source === moduleId && edge.sourceHandle !== nodeKey)
          );
        });
      }
      setNodes(
        produce((nodes) => {
          const module = nodes.find((item) => item.id === moduleId)?.data;
          if (!module) return;
          const item = module.outputs.find((item) => item.key === nodeKey);
          if (item) {
            Object.assign(item, output);
          }
        })
      );
    },
    []
  );

  const copyModule = useCallback((moduleId: string) => {
    setNodes(
      produce((nodes) => {
        const module = nodes.find((module) => moduleId === module.id);
        if (!module) return nodes;

        const newNode = appModule2FlowNode({
          item: {
            avatar: module.data.avatar,
            name: module.data.name,
            description: module.data.description,
            flowType: module.data.flowType,
            inputs: module.data.inputs,
            outputs: module.data.outputs,
            templateType: module.data.templateType,
            moduleId: nanoid(),
            position: {
              x: module.position.x + 200,
              y: module.position.y + 50,
            },
          },
        });

        nodes.push(newNode);
      })
    );
  }, []);

  const deleteModule = useCallback((moduleId: string) => {
    setNodes(
      produce((nodes) => {
        const index = nodes.findIndex((item) => item.id === moduleId);
        nodes.splice(index, 1);
      })
    );
    setEdges((state) =>
      state.filter(
        (edge) => edge.source !== moduleId && edge.target !== moduleId
      )
    );
  }, []);

  const updateNodeValue = useCallback(
    (moduleId: string, nodeKey: string, value: unknown) => {
      setNodes(
        produce((nodes) => {
          const module = nodes.find(
            (item) => item.data.moduleId === moduleId
          )?.data;
          if (module) {
            const node = module.inputs.find((item) => nodeKey === item.key);
            if (node) {
              node.value = value;
            }
          }
        })
      );
    },
    []
  );

  const addNodes = useCallback((...newNodes: Node<FlowModuleItemType>[]) => {
    setNodes(
      produce((nodes) => {
        nodes.push(...newNodes);
      })
    );
  }, []);

  const getNode = useCallback(
    (moduleId: string, nodeKey: string) => {
      const nodes = getAllNodes();
      const module = nodes.find(
        (item) => item.data.moduleId === moduleId
      )?.data;
      return module?.inputs.find((item) => nodeKey === item.key);
    },
    [getAllNodes]
  );

  const addModuleInput = useCallback(
    (moduleId: string, input: FlowNodeInputItemType) => {
      setNodes(
        produce((nodes) => {
          const module = nodes.find(
            (node) => node.data.moduleId === moduleId
          )?.data;
          if (module) {
            const hadExisted = !!module.inputs.find(
              (item) => item.key === input.key
            );
            if (hadExisted) {
              message.warning("该key值已经存在");
              return;
            }
            module.inputs.push(input);
          }
        })
      );
    },
    []
  );

  const addModuleOutput = useCallback(
    (moduleId: string, output: FlowNodeOutputItemType) => {
      setNodes(
        produce((nodes) => {
          const module = nodes.find(
            (node) => node.data.moduleId === moduleId
          )?.data;
          if (module) {
            const hadExisted = !!module.outputs.find(
              (item) => item.key === output.key
            );
            if (hadExisted) {
              message.warning("该key值已经存在");
              return;
            }
            module.outputs.push(output);
          }
        })
      );
    },
    []
  );

  const updateModuleInput = useCallback(
    (moduleId: string, nodeKey: string, input: FlowNodeInputItemType) => {
      setNodes(
        produce((nodes) => {
          const module = nodes.find(
            (node) => node.data.moduleId === moduleId
          )?.data;
          if (module) {
            const index = module.inputs.findIndex(
              (input) => input.key === nodeKey
            );
            if (module.inputs[index].key !== input.key) {
              const hadExisted = !!module.inputs.find(
                (item) => item.key === input.key
              );
              if (hadExisted) {
                message.warning("该key值已经存在");
                return;
              }
            }
            module.inputs.splice(index, 1, input);
          }
        })
      );
      if (input.key !== nodeKey) {
        setEdges((edges) => {
          return edges.filter(
            (edge) =>
              edge.target !== moduleId ||
              (edge.target === moduleId && edge.targetHandle !== nodeKey)
          );
        });
      }
    },
    []
  );

  const deleteModuleInput = useCallback((moduleId: string, nodeKey: string) => {
    setNodes(
      produce((nodes) => {
        const module = nodes.find(
          (node) => node.data.moduleId === moduleId
        )?.data;
        if (module) {
          module.inputs = module.inputs.filter((node) => node.key !== nodeKey);
        }
      })
    );
    setEdges((edges) => {
      return edges.filter(
        (edge) =>
          edge.target !== moduleId ||
          (edge.target === moduleId && edge.targetHandle !== nodeKey)
      );
    });
  }, []);

  const initData = useCallback((modules: ModuleItemType[]) => {
    const edges = appModule2FlowEdge({
      modules,
    });
    setEdges(edges);

    setNodes(modules.map((item) => appModule2FlowNode({ item })));
  }, []);

  const appendModules = useCallback((modules: ModuleItemType[]) => {
    const edges = appModule2FlowEdge({
      modules,
    });
    const nodes = modules.map((item) => appModule2FlowNode({ item }));
    setEdges(
      produce((state) => {
        state.push(...edges);
      })
    );
    setNodes(
      produce((state) => {
        state.push(...nodes);
      })
    );
  }, []);

  const delEdge = useCallback((id: string) => {
    setEdges((edges) => {
      return edges.filter((item) => item.id !== id);
    });
  }, []);

  const delEdgeByTargetHandle = useCallback(
    (target: string, targetHandle: string) => {
      setEdges((edges) => {
        return edges.filter((edge) => {
          return edge.target !== target && edge.targetHandle !== targetHandle;
        });
      });
    },
    []
  );

  return {
    onNodesChange,
    updateNodeValue,
    deleteModule,
    copyModule,
    addNodes,
    getNode,
    onEdgesChange,
    // onConnect,
    splitToolInputs,
    deleteModuleOutput,
    updateModuleOutput,
    addModuleInput,
    updateModuleInput,
    deleteModuleInput,
    addModuleOutput,
    setEdges,
    hasToolNode,
    updateModule,
    initData,
    mode,
    appendModules,
    delEdge,
    getAllEdges,
    getAllNodes,
    templates,
    llmModels,
    systemVariables,
    useablePlugins,
    delEdgeByTargetHandle,
  };
}
